import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Shell } from "@/components/control-tower/Layout";
import {
  getKBTypes,
  getKBEquipmentType,
  getKBZoneType,
  getKBSensorType,
  type KBEquipmentSummary,
  type KBEquipmentType,
  type KBBehaviorDef,
  type KBZoneType,
  type KBZoneConnectableEquipment,
  type KBService,
  type KBZoneSummary,
  type KBSensorSummary,
  type KBSensorType,
  type KBSensorVariants,
  type KBQualityCheck,
  type KBDataPoint,
} from "@/lib/api/agent";
import { useEquipmentTypeMeta } from "@/lib/equipment-type-meta";
import { cn } from "@/lib/utils";
import { cardBase, cardSurface, cardTonePill } from "@/lib/card-styles";
import { requireConfigurator } from "@/lib/route-guards";
import {
  EquipmentKbDetail,
  equipmentKbIaAdapterFor,
} from "@/components/admin/EquipmentKbDetail";

export const Route = createFileRoute("/admin/knowledge-base")({
  // Configurator-only surface (UX/defense-in-depth; the server /kb auth is the real gate).
  beforeLoad: () => requireConfigurator(),
  component: KnowledgeBasePage,
});

// ── Shared helpers ────────────────────────────────────────────────────────────

function DataPointChip({
  id,
  nameFi,
  unit,
  criticality,
}: {
  id: string;
  nameFi: string;
  unit: string;
  criticality: string;
}) {
  return (
    <span
      className={cn(
        "rounded px-1.5 py-0.5 text-[10px] font-mono border",
        criticality === "required"
          ? "border-rose-500/30 bg-rose-500/5 text-rose-400"
          : criticality === "recommended"
            ? "border-warning/30 bg-warning-subtle text-warning-subtle-foreground"
            : "border-border bg-muted/40 text-muted-foreground",
      )}
      title={`${nameFi}${unit ? ` (${unit})` : ""}`}
    >
      {id}
    </span>
  );
}

function CriticalityLegend() {
  return (
    <div className="flex gap-3 text-[10px] text-muted-foreground pt-1">
      <span className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-sm bg-rose-500/20 border border-rose-500/30" />
        vaadittu
      </span>
      <span className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-sm bg-warning-subtle border border-warning/30" />
        suositeltu
      </span>
      <span className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-sm bg-muted border border-border" />
        valinnainen
      </span>
    </div>
  );
}

type SignalMeta = { name_fi: string; unit: string; criticality: string };

/** Resolve component + shared sensor metadata for behavior required-signal chips. */
function equipmentSignalIndex(kb: KBEquipmentType): Map<string, SignalMeta> {
  const byId = new Map<string, SignalMeta>();
  for (const dp of kb.shared_data_points ?? []) {
    byId.set(dp.id, {
      name_fi: dp.name_fi,
      unit: dp.unit,
      criticality: dp.criticality,
    });
  }
  for (const c of kb.components) {
    for (const dp of c.data_points) {
      byId.set(dp.id, {
        name_fi: dp.name_fi,
        unit: dp.unit,
        criticality: dp.criticality,
      });
    }
  }
  return byId;
}

function SharedSignalsSection({ kb }: { kb: KBEquipmentType }) {
  const shared = kb.shared_data_points ?? [];
  if (shared.length === 0) return null;
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        Laitteen signaalit · {shared.length} kpl
      </p>
      <div className="flex flex-wrap gap-1.5">
        {shared.map((dp) => (
          <DataPointChip
            key={dp.id}
            id={dp.id}
            nameFi={dp.name_fi}
            unit={dp.unit}
            criticality={dp.criticality}
          />
        ))}
      </div>
    </div>
  );
}

function BehaviorRequiredSignals({
  behavior,
  signalById,
}: {
  behavior: KBBehaviorDef;
  signalById: Map<string, SignalMeta>;
}) {
  if (behavior.required_data_point_ids.length === 0) return null;
  return (
    <div>
      <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
        Signaalit
      </p>
      <div className="flex flex-wrap gap-1">
        {behavior.required_data_point_ids.map((dpId) => {
          const meta = signalById.get(dpId);
          return (
            <DataPointChip
              key={dpId}
              id={dpId}
              nameFi={meta?.name_fi ?? dpId}
              unit={meta?.unit ?? ""}
              criticality={meta?.criticality ?? "required"}
            />
          );
        })}
      </div>
    </div>
  );
}

function BehaviorsWithSignalsSection({
  behaviors,
  signalById,
}: {
  behaviors: KBBehaviorDef[];
  signalById: Map<string, SignalMeta>;
}) {
  if (behaviors.length === 0) return null;
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        Laitteen toiminnot
      </p>
      <div className="space-y-1.5">
        {behaviors.map((b) => (
          <div
            key={b.id}
            className="rounded-lg border border-border px-3 py-2 flex items-start gap-2"
          >
            <div className="flex-1 min-w-0 space-y-1.5">
              <div>
                <p className="text-xs font-medium">{b.name_fi}</p>
                {b.description_fi && (
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                    {b.description_fi}
                  </p>
                )}
              </div>
              <BehaviorRequiredSignals behavior={b} signalById={signalById} />
            </div>
            {b.full_potential_weight > 0 && (
              <span className="shrink-0 text-[10px] text-muted-foreground">
                {b.full_potential_weight} p
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function normalizeSensorTypeKey(value: string): string {
  return value.toLowerCase().replace(/_/g, "-").replace(/ /g, "-");
}

/** Prefer this equipment's bound points; fall back to KB-wide sensor binding list. */
function qualityCheckSignalsFor(
  sensorKb: KBSensorType,
  equipmentKb: KBEquipmentType | null,
): KBDataPoint[] {
  if (equipmentKb) {
    const key = normalizeSensorTypeKey(sensorKb.device_type);
    const local = [
      ...(equipmentKb.shared_data_points ?? []),
      ...equipmentKb.components.flatMap((c) => c.data_points),
    ].filter(
      (dp) =>
        dp.sensor_type != null &&
        normalizeSensorTypeKey(dp.sensor_type) === key,
    );
    if (local.length > 0) return local;
  }
  return sensorKb.bound_data_points ?? [];
}

function QualityChecksSection({
  checks,
  signals,
}: {
  checks: KBQualityCheck[];
  signals: KBDataPoint[];
}) {
  if (checks.length === 0) return null;
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        Laaduntarkistukset · {checks.length} kpl
      </p>
      <div className="space-y-2">
        {checks.map((qc) => (
          <div
            key={qc.id}
            className="rounded-lg border border-border bg-muted/20 px-3 py-2 space-y-1.5"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium">{qc.name_fi}</span>
              <div className="flex items-center gap-1 shrink-0">
                {qc.optional && (
                  <span className="rounded px-1.5 py-0.5 text-[9px] font-medium border border-border text-muted-foreground leading-none">
                    valinnainen
                  </span>
                )}
                <span
                  className={cn(
                    "rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide leading-none",
                    qc.severity === "fault"
                      ? "bg-rose-500/15 text-rose-400 border border-rose-500/30"
                      : "bg-warning-subtle text-warning-subtle-foreground border border-warning/30",
                  )}
                >
                  {qc.severity === "fault" ? "vika" : "varoitus"}
                </span>
              </div>
            </div>
            {(qc.description_fi || qc.description_en) && (
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                {qc.description_fi || qc.description_en}
              </p>
            )}
            <p className="text-[10px] text-muted-foreground font-mono">
              {qc.algorithm_type}
            </p>
            {qc.suppress_when_count > 0 && (
              <p className="text-[10px] text-muted-foreground">
                {qc.suppress_when_count} poikkeus
                {qc.suppress_when_count > 1 ? "ta" : ""}
              </p>
            )}
            {signals.length > 0 && (
              <div>
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  Signaalit
                </p>
                <div className="flex flex-wrap gap-1">
                  {signals.map((dp) => (
                    <DataPointChip
                      key={dp.id}
                      id={dp.id}
                      nameFi={dp.name_fi}
                      unit={dp.unit}
                      criticality={dp.criticality}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function VariantsSection({ variants }: { variants: KBSensorVariants }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        {variants.name_fi} · {variants.choices.length} vaihtoehtoa
      </p>
      <div className="space-y-1.5">
        {variants.choices.map((c) => (
          <div
            key={c.id}
            className="rounded-lg border border-border bg-muted/20 px-3 py-2"
          >
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-xs font-medium">{c.name_fi}</span>
              <span className="text-[10px] font-mono text-muted-foreground">
                {c.id}
              </span>
            </div>
            {c.description_fi && (
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                {c.description_fi}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── KB detail panels ──────────────────────────────────────────────────────────

function EquipmentDetailHeader({
  icon,
  label,
  equipmentType,
  description,
}: {
  icon: string;
  label: string;
  equipmentType: string;
  description?: string;
}) {
  return (
    <div className="px-5 py-4 border-b border-border shrink-0 space-y-2">
      <div className="flex items-center gap-3">
        <span className="text-2xl leading-none">{icon}</span>
        <div>
          <p className="text-base font-semibold">{label}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5 font-mono">
            {equipmentType}
          </p>
        </div>
      </div>
      {description && (
        <p className="text-sm text-muted-foreground leading-relaxed">
          {description}
        </p>
      )}
    </div>
  );
}

function EquipmentDetail({ equipmentType }: { equipmentType: string }) {
  const [kb, setKb] = useState<KBEquipmentType | null>(null);

  useEffect(() => {
    setKb(null);
    getKBEquipmentType(equipmentType)
      .then(setKb)
      .catch(() => setKb(null));
  }, [equipmentType]);

  const icon = kb?.icon ?? "⚙️";
  const label = kb?.name_fi ?? equipmentType;
  const header = (
    <EquipmentDetailHeader
      icon={icon}
      label={label}
      equipmentType={equipmentType}
      description={kb?.description_fi || kb?.description_en}
    />
  );

  // Every equipment type now opens the live Vaikutusketjut / Komponentit /
  // Riippuvuudet screen — `equipmentKbIaAdapterFor` always resolves an
  // adapter (AHU's own, or the generic payload-driven one as default). The
  // old flat shared-signal-chips / ungrouped-Komponentit / Laitteen toiminnot
  // body is retired; only the loading state renders outside EquipmentKbDetail.
  if (!kb) {
    return (
      <div className={cn(cardBase, "overflow-hidden h-full flex flex-col")}>
        {header}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          <p className="text-xs text-muted-foreground italic">
            Ladataan tietopohja…
          </p>
        </div>
      </div>
    );
  }

  return (
    <EquipmentKbDetail
      header={header}
      kb={kb}
      adapter={equipmentKbIaAdapterFor(equipmentType)}
    />
  );
}

function ZoneDetail({ zoneType, icon }: { zoneType: string; icon: string }) {
  const [kb, setKb] = useState<KBZoneType | null>(null);

  useEffect(() => {
    setKb(null);
    getKBZoneType(zoneType)
      .then(setKb)
      .catch(() => setKb(null));
  }, [zoneType]);

  const label =
    kb?.name_fi ??
    zoneType.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className={cn(cardBase, "overflow-hidden h-full flex flex-col")}>
      <div className="px-5 py-4 border-b border-border shrink-0 flex items-center gap-3">
        <span className="text-2xl leading-none">{icon}</span>
        <div>
          <p className="text-base font-semibold">{label}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5 font-mono">
            {zoneType}
          </p>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        {!kb && (
          <p className="text-xs text-muted-foreground italic">
            Ladataan tietopohja…
          </p>
        )}
        {kb && kb.description_fi && (
          <p className="text-sm text-muted-foreground leading-relaxed">
            {kb.description_fi}
          </p>
        )}
        {kb && kb.data_points.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Signaalit · {kb.total_weight} pistettä yhteensä
            </p>
            <div className="space-y-1.5">
              {kb.data_points.map((dp) => (
                <div
                  key={dp.id}
                  className="py-1.5 border-b border-border/40 last:border-0"
                >
                  <div className="flex items-center gap-2">
                    <DataPointChip
                      id={dp.id}
                      nameFi={dp.name_fi}
                      unit={dp.unit}
                      criticality={dp.criticality}
                    />
                    <span className="text-xs text-foreground flex-1">
                      {dp.name_fi}
                    </span>
                    {dp.unit && (
                      <span className="text-[10px] text-muted-foreground">
                        {dp.unit}
                      </span>
                    )}
                    {dp.full_potential_weight > 0 && (
                      <span className="text-[10px] text-muted-foreground shrink-0">
                        {dp.full_potential_weight} p
                      </span>
                    )}
                  </div>
                  {dp.enables.length > 0 && (
                    <div className="mt-1.5 ml-1 space-y-1">
                      {dp.enables.map((e) => (
                        <div
                          key={`${e.equipment_type}:${e.behavior_id}`}
                          className={cn(
                            cardSurface,
                            "flex flex-wrap items-start gap-x-2 gap-y-0.5 px-2.5 py-1.5",
                          )}
                        >
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 shrink-0">
                            Avaa
                          </span>
                          <span className="text-[11px] font-medium text-foreground">
                            {e.behavior_name_fi}
                            <span className="text-muted-foreground">
                              {" · "}
                              {e.equipment_name_fi}
                            </span>
                          </span>
                          {e.description_fi && (
                            <span className="w-full text-[10.5px] text-muted-foreground leading-snug">
                              {e.description_fi}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {kb && kb.connectable_equipment.length > 0 && (
          <ZoneConnectivitySection connections={kb.connectable_equipment} />
        )}
        {kb && kb.criteria_count > 0 && (
          <p className="text-[11px] text-muted-foreground">
            {kb.criteria_count} operatiivista kriteeriä määritelty.
          </p>
        )}
        {kb && <CriticalityLegend />}
      </div>
    </div>
  );
}

function ZoneConnectivitySection({
  connections,
}: {
  connections: KBZoneConnectableEquipment[];
}) {
  const eqMeta = useEquipmentTypeMeta();
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        Mihin laitteisiin tämä vyöhyketyyppi liittyy
      </p>
      <div className="space-y-3">
        {connections.map((connection) => {
          const norm = connection.equipment_type.replace(/-/g, "_");
          return (
            <div
              key={`${connection.equipment_type}-${connection.dependency_type}`}
              className="space-y-1.5"
            >
              <div className="flex items-center gap-1.5 text-xs text-foreground">
                <span>{eqMeta[norm]?.icon ?? "⚙️"}</span>
                <span>
                  {eqMeta[norm]?.name_fi ?? connection.equipment_type}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  · {connection.dependency_type}
                </span>
              </div>
              <div className="space-y-1 pl-1">
                {connection.equipment_publishes.map((signal) => (
                  <div
                    key={signal.zone_receives_as}
                    className="flex items-center gap-2 py-1 border-b border-border/40 last:border-0"
                  >
                    <span className="text-xs text-foreground flex-1">
                      {signal.description_fi || signal.zone_receives_as}
                    </span>
                    <RelationBadge relation={signal.relation} />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RelationBadge({ relation }: { relation: "compare" | "substitute" }) {
  if (relation === "substitute") {
    return (
      <span
        className={cn(
          "shrink-0 rounded-full border px-2 py-0.5 text-[10px]",
          cardTonePill.learning,
        )}
        title="Korvaava lukema — ei vielä käytössä vyöhykkeen omana arvona"
      >
        korvaava (ei aktiivinen)
      </span>
    );
  }
  return (
    <span
      className={cn(
        "shrink-0 rounded-full border px-2 py-0.5 text-[10px]",
        cardTonePill.ok,
      )}
      title="Näytetään rinnakkain vyöhykkeen oman arvon kanssa"
    >
      vertailu
    </span>
  );
}

function ServiceDetail({ service }: { service: KBService }) {
  const eqMeta = useEquipmentTypeMeta();
  return (
    <div className={cn(cardBase, "overflow-hidden h-full flex flex-col")}>
      <div className="px-5 py-4 border-b border-border shrink-0 flex items-center gap-3">
        <span className="text-2xl leading-none">⚡</span>
        <div>
          <p className="text-base font-semibold">{service.name_fi}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5 font-mono">
            {service.id}
          </p>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        {service.description_fi && (
          <p className="text-sm text-muted-foreground leading-relaxed">
            {service.description_fi}
          </p>
        )}
        {service.compatible_equipment.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Yhteensopivat laitteet
            </p>
            <div className="flex flex-wrap gap-1.5">
              {service.compatible_equipment.map((eq) => {
                const norm = eq.replace(/-/g, "_");
                return (
                  <span
                    key={eq}
                    className="rounded-full border border-border px-2.5 py-1 text-xs flex items-center gap-1.5"
                  >
                    <span>{eqMeta[norm]?.icon ?? "⚙️"}</span>
                    {eqMeta[norm]?.name_fi ?? eq}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SensorDetail({ sensorType }: { sensorType: string }) {
  const [kb, setKb] = useState<KBSensorType | null>(null);

  useEffect(() => {
    setKb(null);
    getKBSensorType(sensorType)
      .then(setKb)
      .catch(() => setKb(null));
  }, [sensorType]);

  const label = kb?.name_fi ?? sensorType.replace(/-/g, " ");
  const icon = kb ? (FIELD_DEVICE_ICON[kb.device_class] ?? "📡") : "📡";

  return (
    <div className={cn(cardBase, "overflow-hidden h-full flex flex-col")}>
      <div className="px-5 py-4 border-b border-border shrink-0 flex items-center gap-3">
        <span className="text-2xl leading-none">{icon}</span>
        <div>
          <p className="text-base font-semibold">{label}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5 font-mono">
            {sensorType}
          </p>
        </div>
        {kb && (
          <span className="ml-auto text-xs text-muted-foreground font-mono border border-border rounded px-2 py-0.5">
            {kb.unit}
          </span>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        {!kb && (
          <p className="text-xs text-muted-foreground italic">
            Ladataan tietopohja…
          </p>
        )}
        {kb && (kb.description_fi || kb.description_en) && (
          <p className="text-sm text-muted-foreground leading-relaxed">
            {kb.description_fi || kb.description_en}
          </p>
        )}
        {kb?.variants && <VariantsSection variants={kb.variants} />}
        {kb && (kb.bounds_min !== null || kb.bounds_max !== null) && (
          <div className="flex gap-4 text-xs">
            {kb.bounds_min !== null && (
              <span className="text-muted-foreground">
                Min:{" "}
                <span className="font-mono text-foreground">
                  {kb.bounds_min} {kb.unit}
                </span>
              </span>
            )}
            {kb.bounds_max !== null && (
              <span className="text-muted-foreground">
                Max:{" "}
                <span className="font-mono text-foreground">
                  {kb.bounds_max} {kb.unit}
                </span>
              </span>
            )}
          </div>
        )}
        {kb && (
          <QualityChecksSection
            checks={kb.quality_checks}
            signals={qualityCheckSignalsFor(kb, null)}
          />
        )}
      </div>
    </div>
  );
}

// ── Nav item ──────────────────────────────────────────────────────────────────

function NavItem({
  icon,
  label,
  sublabel,
  isSelected,
  onSelect,
}: {
  icon: string;
  label: string;
  sublabel?: string;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "w-full text-left rounded-lg px-2 py-2 transition-colors flex items-center gap-2",
        isSelected
          ? "bg-primary/10 text-primary"
          : "hover:bg-muted/60 text-foreground",
      )}
    >
      <span className="text-base leading-none shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <span className="text-sm block truncate">{label}</span>
        {sublabel && (
          <span className="text-[10px] text-muted-foreground font-mono truncate block">
            {sublabel}
          </span>
        )}
      </div>
    </button>
  );
}

// ── Field device groups ───────────────────────────────────────────────────────

const FIELD_DEVICE_GROUPS: {
  class: KBSensorSummary["device_class"];
  label: string;
  icon: string;
}[] = [
  { class: "meter", label: "Mittarit", icon: "🔢" },
  { class: "sensor", label: "Anturit", icon: "📡" },
  { class: "actuator", label: "Toimilaitteet", icon: "🔘" },
  { class: "drive", label: "Taajuusmuuttajat", icon: "⚙️" },
];

const FIELD_DEVICE_ICON: Record<KBSensorSummary["device_class"], string> = {
  meter: "🔢",
  sensor: "📡",
  actuator: "🔘",
  drive: "⚙️",
};

// Combined sensor+equipment catalog rows. Keyed by sensor id; value is the
// equipment folder. Meter-class folders must not also appear under Talotekniikka.
const METER_EQUIPMENT_TYPE: Record<string, string> = {
  "electricity-meter": "electricity-meter",
  "heat-meter": "heat-meter",
  "water-meter": "water-meter",
};

// Equipment types that also have a field-device (sensor) KB entry, and so must
// be hidden from Talotekniikka to avoid appearing twice — shown only under
// Kenttälaitteet. Superset of METER_EQUIPMENT_TYPE's equipment folders: meters
// open the combined MeterDetail view, other field devices (temperature-sensor,
// people-counter) keep their equipment folder for catalog completeness but open
// the plain sensor detail.
const FIELD_DEVICE_EQUIPMENT_TYPES = new Set<string>([
  ...Object.values(METER_EQUIPMENT_TYPE),
  "temperature-sensor",
  "people-counter",
  "co2-sensor",
]);

// ── Meter detail (quality checks + equipment KB combined) ─────────────────────

function MeterDetail({
  sensor,
  equipmentType,
}: {
  sensor: KBSensorSummary;
  equipmentType: string;
}) {
  const [sensorKb, setSensorKb] = useState<KBSensorType | null>(null);
  const [equipmentKb, setEquipmentKb] = useState<KBEquipmentType | null>(null);

  useEffect(() => {
    setSensorKb(null);
    setEquipmentKb(null);
    getKBSensorType(sensor.id)
      .then(setSensorKb)
      .catch(() => setSensorKb(null));
    getKBEquipmentType(equipmentType)
      .then(setEquipmentKb)
      .catch(() => setEquipmentKb(null));
  }, [sensor.id, equipmentType]);

  const label = equipmentKb?.name_fi ?? sensorKb?.name_fi ?? sensor.name_fi;
  const signalById = equipmentKb
    ? equipmentSignalIndex(equipmentKb)
    : new Map<string, SignalMeta>();

  return (
    <div className={cn(cardBase, "overflow-hidden h-full flex flex-col")}>
      <div className="px-5 py-4 border-b border-border shrink-0 flex items-center gap-3">
        <span className="text-2xl leading-none">
          {FIELD_DEVICE_ICON[sensor.device_class]}
        </span>
        <div>
          <p className="text-base font-semibold">{label}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5 font-mono">
            {sensor.id}
          </p>
        </div>
        {sensorKb && (
          <span className="ml-auto text-xs text-muted-foreground font-mono border border-border rounded px-2 py-0.5">
            {sensorKb.unit}
          </span>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        {!sensorKb && !equipmentKb && (
          <p className="text-xs text-muted-foreground italic">
            Ladataan tietopohja…
          </p>
        )}
        {(sensorKb?.description_fi || sensorKb?.description_en) && (
          <p className="text-sm text-muted-foreground leading-relaxed">
            {sensorKb.description_fi || sensorKb.description_en}
          </p>
        )}
        {sensorKb?.variants && <VariantsSection variants={sensorKb.variants} />}

        {equipmentKb && <SharedSignalsSection kb={equipmentKb} />}

        {equipmentKb && equipmentKb.components.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Komponentit · {equipmentKb.total_weight} pistettä yhteensä
            </p>
            <div className="space-y-2">
              {equipmentKb.components.map((c) => (
                <div
                  key={c.id}
                  className="rounded-lg border border-border bg-muted/20 px-3 py-2"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium flex items-center gap-1.5">
                      {c.name_fi}
                      {c.optional && (
                        <span className="rounded px-1 py-0.5 text-[9px] font-medium border border-border text-muted-foreground leading-none">
                          valinnainen
                        </span>
                      )}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {c.full_potential_weight} p
                    </span>
                  </div>
                  {c.description_fi && (
                    <p className="text-[11px] text-muted-foreground mb-1.5 leading-relaxed">
                      {c.description_fi}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1">
                    {c.data_points.map((dp) => (
                      <DataPointChip
                        key={dp.id}
                        id={dp.id}
                        nameFi={dp.name_fi}
                        unit={dp.unit}
                        criticality={dp.criticality}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {sensorKb && (
          <QualityChecksSection
            checks={sensorKb.quality_checks}
            signals={qualityCheckSignalsFor(sensorKb, equipmentKb)}
          />
        )}

        {equipmentKb && (
          <BehaviorsWithSignalsSection
            behaviors={equipmentKb.behaviors}
            signalById={signalById}
          />
        )}

        {equipmentKb && <CriticalityLegend />}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

type Selection =
  | { kind: "equipment"; type: string }
  | { kind: "zone"; zone: KBZoneSummary }
  | { kind: "service"; service: KBService }
  | { kind: "sensor"; sensor: KBSensorSummary }
  | { kind: "meter"; sensor: KBSensorSummary; equipmentType: string }
  | null;

function KnowledgeBasePage() {
  const [equipmentTypes, setEquipmentTypes] = useState<KBEquipmentSummary[]>(
    [],
  );
  const [zoneTypes, setZoneTypes] = useState<KBZoneSummary[]>([]);
  const [services, setServices] = useState<KBService[]>([]);
  const [sensorTypes, setSensorTypes] = useState<KBSensorSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Selection>(null);

  useEffect(() => {
    getKBTypes()
      .then((types) => {
        setEquipmentTypes(types.equipment_types);
        setZoneTypes(types.zone_types);
        setServices(types.services);
        setSensorTypes(types.sensor_types ?? []);
        if (types.equipment_types.length > 0) {
          setSelected({
            kind: "equipment",
            type: types.equipment_types[0].equipment_type,
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <Shell
      title="Tietopohja"
      subtitle="Laitteiden, vyöhykkeiden ja palveluiden tietopohjakuvaukset"
    >
      <div className="flex gap-4" style={{ height: "calc(100vh - 10rem)" }}>
        {/* Left nav */}
        <div className={cn("w-56 shrink-0 overflow-y-auto", cardBase)}>
          {loading ? (
            <div className="p-4 text-xs text-muted-foreground">Ladataan…</div>
          ) : (
            <div className="p-2 space-y-4">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2 py-1.5">
                  Talotekniikka
                </p>
                {equipmentTypes
                  .filter(
                    (s) => !FIELD_DEVICE_EQUIPMENT_TYPES.has(s.equipment_type),
                  )
                  .map((s) => (
                    <NavItem
                      key={s.equipment_type}
                      icon={s.icon || "⚙️"}
                      label={s.name_fi || s.equipment_type}
                      isSelected={
                        selected?.kind === "equipment" &&
                        selected.type === s.equipment_type
                      }
                      onSelect={() =>
                        setSelected({
                          kind: "equipment",
                          type: s.equipment_type,
                        })
                      }
                    />
                  ))}
              </div>

              {zoneTypes.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2 py-1.5">
                    Vyöhykkeet
                  </p>
                  {zoneTypes.map((zone) => (
                    <NavItem
                      key={zone.id}
                      icon={zone.icon || "📍"}
                      label={zone.name_fi}
                      isSelected={
                        selected?.kind === "zone" &&
                        selected.zone.id === zone.id
                      }
                      onSelect={() => setSelected({ kind: "zone", zone })}
                    />
                  ))}
                </div>
              )}

              {sensorTypes.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2 py-1.5">
                    Kenttälaitteet
                  </p>
                  {FIELD_DEVICE_GROUPS.map(({ class: cls, label, icon }) => {
                    const items = sensorTypes.filter(
                      (s) => s.device_class === cls,
                    );
                    if (items.length === 0) return null;
                    return (
                      <div key={cls}>
                        <p className="text-[9px] font-medium uppercase tracking-widest text-muted-foreground/60 px-2 pt-1.5 pb-0.5">
                          {label}
                        </p>
                        {items.map((sensor) => {
                          const meterEquipmentType =
                            METER_EQUIPMENT_TYPE[sensor.id];
                          const isSelected =
                            (selected?.kind === "sensor" &&
                              selected.sensor.id === sensor.id) ||
                            (selected?.kind === "meter" &&
                              selected.sensor.id === sensor.id);
                          const catalogLabel = meterEquipmentType
                            ? (equipmentTypes.find(
                                (e) => e.equipment_type === meterEquipmentType,
                              )?.name_fi ?? sensor.name_fi)
                            : sensor.name_fi;
                          return (
                            <NavItem
                              key={sensor.id}
                              icon={icon}
                              label={catalogLabel}
                              isSelected={isSelected}
                              onSelect={() =>
                                meterEquipmentType
                                  ? setSelected({
                                      kind: "meter",
                                      sensor,
                                      equipmentType: meterEquipmentType,
                                    })
                                  : setSelected({ kind: "sensor", sensor })
                              }
                            />
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}

              {services.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2 py-1.5">
                    Palvelut
                  </p>
                  {services.map((svc) => (
                    <NavItem
                      key={svc.id}
                      icon="⚡"
                      label={svc.name_fi}
                      isSelected={
                        selected?.kind === "service" &&
                        selected.service.id === svc.id
                      }
                      onSelect={() =>
                        setSelected({ kind: "service", service: svc })
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Detail panel */}
        <div className="flex-1 min-w-0">
          {!selected && (
            <div className="flex items-center justify-center h-full rounded-xl border border-dashed border-border text-sm text-muted-foreground">
              Valitse tyyppi vasemmalta
            </div>
          )}
          {selected?.kind === "equipment" && (
            <EquipmentDetail
              key={selected.type}
              equipmentType={selected.type}
            />
          )}
          {selected?.kind === "zone" && (
            <ZoneDetail
              key={selected.zone.id}
              zoneType={selected.zone.id}
              icon={selected.zone.icon || "📍"}
            />
          )}
          {selected?.kind === "sensor" && (
            <SensorDetail
              key={selected.sensor.id}
              sensorType={selected.sensor.id}
            />
          )}
          {selected?.kind === "meter" && (
            <MeterDetail
              key={selected.sensor.id}
              sensor={selected.sensor}
              equipmentType={selected.equipmentType}
            />
          )}
          {selected?.kind === "service" && (
            <ServiceDetail
              key={selected.service.id}
              service={selected.service}
            />
          )}
        </div>
      </div>
    </Shell>
  );
}
